<template>
  <div class="employees-organization-table">
    <UniversalTable
      :config="tableConfigWithFilters"
      :items="paginatedEmployees"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @onSearch="handleSearch"
      @onSort="handleSort"
      @removeFilter="removeFilter"
      @removeAllFilters="removeAllFilters"
      @onActionClick="handleAddEmployee"
      @drilldown="handleEmployeeDrilldown"
      backend-pagination
      @onRequest="handleRequest"
      :externalCurrentPage="currentPage"
      :items-count="filteredCount"
      :cellContainerMaxWidth="
        tableConfig.tableParams.value.cellContainerMaxWidth
      "
    >
      <template #Dropdown-activator>
        <div class="icon-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </UniversalTable>
    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Employee"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />
    <AddEmployeeDialog
      v-if="!employees.getCurrentEmployee()"
      :show="viewState.state.addEmployeeModal.show"
      @close="viewState.state.addEmployeeModal.show = false"
      @onSave="handleSaveEmployee"
      :propEmployee="null"
    />
    <RenameEmployeeDialog
      v-else
      :show="viewState.state.addEmployeeModal.show"
      @close="viewState.state.addEmployeeModal.show = false"
      @onSave="handleSaveEmployee"
      :propEmployee="
        employees.getCurrentEmployee()
          ? {
              id: employees.getCurrentEmployee()?.id || 0,
              first_name: employees.getCurrentEmployee()?.first_name || '',
              last_name: employees.getCurrentEmployee()?.last_name || '',
              title: employees.getCurrentEmployee()?.title || '',
              email: employees.getCurrentEmployee()?.email || '',
              availability: employees.getCurrentEmployee()?.availability || 0,
              avatar: employees.getCurrentEmployee()?.avatar || '',
              user: employees.getCurrentEmployee()?.user || '',
            }
          : null
      "
    />
    <ActivateEmployee
      :show="viewState.state.activeEmployeeModal.show"
      :employee="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || 'U',
        lastName: employees.getCurrentEmployee()?.last_name || 'S',
      }"
      @onClose="viewState.state.activeEmployeeModal.show = false"
      @onActiveEmployee="handleActivateEmployee"
    />
    <DeleteEmployee
      @onAction="handleDeleteEmployee"
      @onClose="viewState.state.deleteEmployeeModal.show = false"
      :show="viewState.state.deleteEmployeeModal.show"
      :info="viewState.state.deleteEmployeeModal.message"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || 'U',
        lastName: employees.getCurrentEmployee()?.last_name || 'S',
      }"
    />
    <TerminateEmployee
      @onAction="handleTerminateEmployee"
      @onClose="viewState.state.terminateEmployeeModal.show = false"
      :show="viewState.state.terminateEmployeeModal.show"
      :info="viewState.state.terminateEmployeeModal.message"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || 'U',
        lastName: employees.getCurrentEmployee()?.last_name || 'S',
        allData: employees.getCurrentEmployee() || {},
      }"
    />
    <ReActivateEmployee
      :show="viewState.state.reactivateEmployeeModal.show"
      :employee="{
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        ...employees.getCurrentEmployee(),
      }"
      @onClose="viewState.state.reactivateEmployeeModal.show = false"
      @onActiveEmployee="handleReactivateEmployee"
    />
    <EditModalPicture
      :dialog="viewState.state.changePictureModal.show"
      :avatar="employees.getCurrentEmployee()?.avatar || ''"
      @croppedImage="handleChangePicture"
      @closeModal="viewState.state.changePictureModal.show = false"
      @removeModal="handleRemovePicture"
    />
    <GiveAccessToEmployeeModal
      @checkEmail="handleCheckEmail"
      @onSave="handleGiveAccess"
      @onChangeContentType="viewState.state.giveAccessModal.dataType = $event"
      v-model="viewState.state.giveAccessModal.show"
      :contentTypeProp="viewState.state.giveAccessModal.dataType"
      :existingUser="viewState.state.giveAccessModal.existingUser"
      :access_level="viewState.state.giveAccessModal.access_level"
      :showEmailLoadingIcon="viewState.state.giveAccessModal.showEmailLoadingIcon"
      :current-employee="{
        id: employees.getCurrentEmployee()?.id || 0,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        availabilityVal: employees.getCurrentEmployee()?.availability,
        avatar: employees.getCurrentEmployee()?.avatar || '',
        email: employees.getCurrentEmployee()?.email || '',
        primary_position: employees.getCurrentEmployee()?.primary_position,
        preparing_for_positions: employees.getCurrentEmployee()?.preparing_for_positions,
      }"
    />
    <GiveBackAccessToEmployeeModal
      @onSave="handleGiveBackAccess"
      @onChangeContentType="viewState.state.giveAccessModal.dataType = $event"
      v-model="viewState.state.giveBackAccessModal.show"
      :contentTypeProp="viewState.state.giveAccessModal.dataType"
      :existingUser="viewState.state.giveAccessModal.existingUser"
      :showEmailLoadingIcon="viewState.state.giveAccessModal.showEmailLoadingIcon"
      :current-employee="{
        id: employees.getCurrentEmployee()?.id || 0,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
        availabilityVal: employees.getCurrentEmployee()?.availability,
        avatar: employees.getCurrentEmployee()?.avatar || '',
        email: employees.getCurrentEmployee()?.email || '',
      }"
    />
    <ArchivePopUp
      :action="viewState.state.archiveAction"
      type="employee"
      :show="viewState.state.archiveEmployeeModal.show"
      :curretObj="{
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.archiveEmployeeModal.show = false"
      @onArchive="handleArchiveEmployee"
    />
    <ExActionsPopUp
      v-if="viewState.state.suspendEmployeeAccessModal?.show"
      headline="Suspend Access"
      :htmlInfo="viewState.state.suspendEmployeeAccessModal.message"
      actionLabel="Suspend"
      :value="viewState.state.suspendEmployeeAccessModal?.show"
      :destructive="true"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onAction="handleSuspendAccess"
      @input="viewState.state.suspendEmployeeAccessModal.show = false"
    />
    <ExActionsPopUp
      v-if="viewState.state.giveBackEmployeeAccessModal?.show"
      headline="Give back Access"
      :htmlInfo="viewState.state.giveBackEmployeeAccessModal.message"
      actionLabel="Give back Access"
      :value="viewState.state.giveBackEmployeeAccessModal?.show"
      :destructive="false"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onAction="handleGiveBackAccessUser"
      @input="viewState.state.giveBackEmployeeAccessModal.show = false"
    />
    <CannotRenameEmployeeModal
      :show="viewState.state.cannotRenamEmployeeModal.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      @onClose="viewState.state.cannotRenamEmployeeModal.show = false"
      @onAction="viewState.state.cannotRenamEmployeeModal.show = false"
    />
    <ChecklistDrawer
      :isEmployeeChatter="true"
      ref="drawerContainer"
      :drawer="viewState.state?.openChatterModal?.show"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar,
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      :typeValue="
        (employees.getCurrentEmployee()?.first_name || '') +
        ' ' +
        (employees.getCurrentEmployee()?.last_name || '')
      "
      @closeDrawer="viewState.closeEmployeeChatterModal()"
      :type="'Employee'"
      :chatterList="viewState.state?.openChatterModal?.chatterData"
      :totalCheckpointChatter="viewState.state?.openChatterModal?.totalEmployeeChatter"
      @requestMoreCheckpointsChatter="viewState.requestMoreChatter"
    />
    <ExEmployeePrimaryAssignmentWizard
      :label="'Position'"
      :items="viewState.state.primaryPositions.availablePositions"
      :currentEmployee="employees.getCurrentEmployee()"
      :value="viewState.state.assignmentWizard.show"
      :type="'scrollPositionPicker'"
      :isAssigning="viewState.state.assignmentWizard.isAssigning"
      @input="!$event ? viewState.closeAssignmentWizard() : ''"
      @onAssignPosition="
        viewState.onAssignPositionToEmployee({
          position: $event,
          accountId: accountID,
        })
      "
      @load-more="viewState.loadMorePrimaryPositions"
      @search="viewState.searchPrimaryPositions"
      :isLoadingMore="viewState.state.primaryPositions.isLoadingMore"
      :hasMoreData="viewState.state.primaryPositions.hasMoreData"
    />
    <ExEmployeeToPreparePosition
      :label="'Position'"
      :items="viewState.state.preparePositions.availablePositions"
      :currentEmployee="employees.getCurrentEmployee()"
      :value="viewState.state.preparePosition.show"
      :isAssigning="viewState.state.preparePosition.isAssigning"
      @input="!$event ? viewState.closePreparePosition() : ''"
      @onAssignPreparePosition="
        viewState.onAssignPreparePositionToEmployee({
          obj: $event,
          accountId: accountID,
        })
      "
      @load-more="viewState.loadMorePreparePositions"
      @search="viewState.searchPreparePositions"
      :isLoadingMore="viewState.state.preparePositions.isLoadingMore"
      :hasMoreData="viewState.state.preparePositions.hasMoreData"
    />
    <RelievePrimaryAssignment
      v-if="viewState.state.relieveAssignment.show"
      :show="viewState.state.relieveAssignment.show"
      :type="viewState.state.relieveAssignment.type"
      :avatar="{
        picture: employees.getCurrentEmployee()?.avatar || '',
        firstName: employees.getCurrentEmployee()?.first_name || '',
        lastName: employees.getCurrentEmployee()?.last_name || '',
      }"
      :current-position="viewState.state.relieveAssignment.currentPosition?.name || ''"
      @onAction="viewState.relievePrimaryPositionFromEmployee(accountID, $event)"
      @onClose="viewState.closeRelieveAssignment()"
    />
  </div>
</template>

<script setup>
const emit = defineEmits(['drilldown']);

const handleEmployeeDrilldown = (data) => {
  console.log('🟠 EmployeesOrganizationTable.handleEmployeeDrilldown() called', data);
  // Re-emit drilldown event to parent (OrganizationEmployeesTab -> OrganizationPage)
  emit('drilldown', data);
  console.log('🟠 EmployeesOrganizationTable drilldown event emitted');
};
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import { useTableConfig } from "./composables/useTableConfig";

import SearchModal from "@components/SearchBar/SearchModal.vue";
import AddEmployeeDialog from "@components/organizations/employees/AddEmployeeDialog/AddEmployeeDialog.vue";
import RenameEmployeeDialog from "@components/organizations/employees/RenameEmployeeDialog/RenameEmployeeDialog.vue";
import ActivateEmployee from "@components/modals/ActiveEmployee.vue";
import DeleteEmployee from "@components/organizations/employees/DeleteEmployee.vue";
import TerminateEmployee from "@components/organizations/employees/TerminateEmployee.vue";
import ReActivateEmployee from "@components/modals/ReActivateEmployee.vue";
import EditModalPicture from "@components/EditPicture/EditModal.vue";
import GiveAccessToEmployeeModal from "@components/GiveAccessEmployee/GiveAccessToEmployeeModal.vue";
import GiveBackAccessToEmployeeModal from "@components/GiveBackAccessEmployee/GiveBackAccessToEmployeeModal.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import CannotRenameEmployeeModal from "@components/organizations/employees/CannotRenameEmployeeModal.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
import ExEmployeePrimaryAssignmentWizard from "@components/PositionPicker/ExEmployeePrimaryAssignmentWizard.vue";
import ExEmployeeToPreparePosition from "@components/PreparePositionPicker/ExEmployeeToPreparePosition.vue";
import RelievePrimaryAssignment from "@components/organizations/employees/RelievePrimaryAssignment.vue";
import { useEmployeesOrganizationTableViewState } from "@components/EmployeesOrganizationTable/composables/useEmployeesOrganizationTableViewState";
import { useEmployees } from "@/composables/employees/useEmployees";
import { useEmployeeStore } from "@/store/pinia/employee";
import { userStateUpdateSuspend, userStateUpdateGiveBack } from "@/api";

const app = getCurrentInstance();
const { proxy } = app || {};

const props = defineProps({
  employees: {
    type: Array,
    default: () => [],
  },
});

const tableConfig = useTableConfig();
const viewState = useEmployeesOrganizationTableViewState();
const employees = useEmployees();
const employeeStore = useEmployeeStore();
const accountID = ref(JSON.parse(localStorage.getItem("currentAccountID")));

// Pagination and search state
const searchText = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const sortBy = ref("first_name");
const sortDesc = ref(false);

// Sync local searchText with viewState searchText
watch(
  () => viewState.state.filters.searchText,
  (newValue) => {
    if (newValue !== searchText.value) {
      searchText.value = newValue || "";
    }
  },
  { immediate: true }
);

// Use employees from composable/store if available, otherwise use props
const employeesList = computed(() => {
  const storeEmployees = employees.allEmployees.value || employeeStore.getEmployees || [];
  return storeEmployees.length > 0 ? storeEmployees : props.employees;
});

// Watch for changes in employees store
watch(
  () => employees.allEmployees.value,
  () => {
    // Update current page when data changes
    currentPage.value = viewState.state.table.currentPage || 1;
  },
  { deep: true }
);

// Watch for route changes to clear search when navigating away from employees tab
watch(
  () => proxy?.$route,
  (to, from) => {
    if (!proxy?.$route || !from) return;
    
    const currentTab = proxy.$route.params?.tab?.toLowerCase() || "";
    const previousTab = from?.params?.tab?.toLowerCase() || "";
    const currentPath = proxy.$route.path || "";
    const previousPath = from?.path || "";
    
    // Determine if we were on employees tab
    // Check tab param first, then fall back to path checking
    const wasOnEmployeesTab = previousTab === "employees" || 
      (previousPath.includes("/new-organization/employees") && !previousPath.match(/\/new-organization\/employees\/\d+/));
    
    // Determine if we're currently on employees tab
    const isOnEmployeesTab = currentTab === "employees" || 
      (currentPath.includes("/new-organization/employees") && !currentPath.match(/\/new-organization\/employees\/\d+/));
    
    // If we were on employees tab and now we're not, clear the search
    if (wasOnEmployeesTab && !isOnEmployeesTab && viewState.state.filters.searchText) {
      viewState.clearSearch();
      searchText.value = "";
      // Also remove search filters from API
      employees.removeEmployeeFilter("first_name");
      employees.removeEmployeeFilter("last_name");
      employees.removeEmployeeFilter("name");
    }
  },
  { immediate: false }
);

// Add filters to table config
const tableConfigWithFilters = computed(() => {
  return {
    ...tableConfig.tableParams.value,
    filters: {
      values: viewState.state.filters.values,
      searchText: searchText.value,
      searchGhostText: viewState.state.filters.searchGhostText
    },
  };
});

// Apply filters to employees based on available data
const applyFilterLogic = (employees, filters) => {
  if (!filters || filters.length === 0) return employees;

  return employees.filter((emp) => {
    // Extract filter values (handle both string and object formats)
    const filterValues = filters.map(f => typeof f === 'object' ? f.value : f);
    
    // Access filters
    const noAccessFilter = filterValues.find(
      (f) => f === "no_access" || f === "No Access"
    );
    const hasAccessFilter = filterValues.find(
      (f) => f === "has_access" || f === "Has Access"
    );

    if (noAccessFilter) {
      // No access: when access object is null or has_access is false
      if (emp.access !== null && emp.access?.has_access !== false) return false;
    }

    if (hasAccessFilter) {
      // Has access: when access object exists and has_access is true
      if (!emp.access || !emp.access.has_access) return false;
    }

    // Assignment filters
    const hasAssignmentFilter = filterValues.find(
      (f) => f === "has_assignment" || f === "Has Assignments"
    );
    const noAssignmentFilter = filterValues.find(
      (f) => f === "no_assignment" || f === "No Assignments"
    );

    if (hasAssignmentFilter) {
      // Has assignment: employee has primary_position OR preparing_for_positions
      const hasPrimary = emp.primary_position !== null;
      const hasPreparing =
        emp.preparing_for_positions && emp.preparing_for_positions.length > 0;
      if (!hasPrimary && !hasPreparing) return false;
    }

    if (noAssignmentFilter) {
      // No assignment: employee has no primary_position AND no preparing_for_positions
      const hasPrimary = emp.primary_position !== null;
      const hasPreparing =
        emp.preparing_for_positions && emp.preparing_for_positions.length > 0;
      if (hasPrimary || hasPreparing) return false;
    }

    // Archived filters
    const showArchivedFilter = filterValues.find(
      (f) => f === "show_archived" || f === "Show"
    );
    const noArchivedFilter = filterValues.find(
      (f) => f === "no_archived" || f === "Do not Show"
    );
    const statusFilters = filterValues.filter(
      (f) => f === "active" || f === "terminated" || f === "draft"
    );
    const isStatusFilter = statusFilters.length > 0;
    const isShowArchived = !!showArchivedFilter;
    const isNoArchived = !!noArchivedFilter;

    // (1) Both status and show_archived: show all with status (archived and not)
    if (isStatusFilter && isShowArchived) {
      if (!statusFilters.includes(emp.status)) return false;
      return true;
    }
    // (2) Status only: only non-archived with status
    if (isStatusFilter && !isShowArchived) {
      if (emp.archived === true) return false;
      if (!statusFilters.includes(emp.status)) return false;
      return true;
    }
    // (3) Only show_archived (no status): only archived
    if (!isStatusFilter && isShowArchived) {
      if (emp.archived !== true) return false;
      return true;
    }
    // (4) Only no_archived (no status): only non-archived
    if (!isStatusFilter && isNoArchived) {
      if (emp.archived === true) return false;
      return true;
    }
    // (5) No filters or unknown: return all
    return true;
  });
};

// Use employees from store/composable (backend pagination)
// Apply frontend filtering to exclude draft employees when "no_access" filter is active
const paginatedEmployees = computed(() => {
  const employees = employeesList.value || [];
  
  // Check if "no_access" filter is active
  const hasNoAccessFilter = viewState.state.filters.values?.some(
    (f) => {
      const filterValue = typeof f === 'object' ? f.value : f;
      return filterValue === "no_access" || filterValue === "No Access";
    }
  );
  
  // If "no_access" filter is active, filter out draft employees on frontend
  // This ensures only active employees without access are shown
  if (hasNoAccessFilter) {
    return employees.filter((emp) => emp.status === "active");
  }
  
  return employees;
});

// Get total count from store pagination
// The API returns the correct total count based on applied filters, so we should use that
// instead of counting the current page's results
const filteredCount = computed(() => {
  // Always use the total count from the API response (employeeStore.pagination?.totalCount)
  // This ensures the count matches what the API returns based on the applied filters
  // The API correctly filters by "no_access" and "active" status, so the totalCount will be accurate
  return employeeStore.pagination?.totalCount || employeesList.value.length || 0;
});

const handleSearch = (search) => {
  searchText.value = search;
  currentPage.value = 1;
  // Use viewState's handleSearch which calls the API
  viewState.handleSearch(search);
};

const handleSort = (sortData) => {
  // Use viewState's handleEmployeeSort which calls the API
  // sortData comes from UniversalTable as { field, descending, ascending }
  viewState.handleEmployeeSort({
    field: sortData.field,
    descending: sortData.descending
  });
};

const handleRequest = async ({ page, itemsPerPage: perPage }) => {
  currentPage.value = page;
  itemsPerPage.value = perPage || 10;
  // Use viewState's handleRequest which calls the API
  await viewState.handleRequest({ page, itemsPerPage: perPage || 10 });
};

const applyFilters = async (filters) => {
  // Use viewState's applyFilters which handles API calls
  await viewState.applyFilters(filters);
  currentPage.value = 1;
};

const removeFilter = async (filter) => {
  // Remove the filter from state first
  const filterValue = typeof filter === 'object' ? filter.value : filter;
  viewState.state.filters.values = viewState.state.filters.values.filter(
    (f) => {
      const fValue = typeof f === 'object' ? f.value : f;
      return fValue !== filterValue;
    }
  );
  
  // Use viewState's removeFilters which handles API calls
  await viewState.removeFilters(filter);
  currentPage.value = 1;
};

const removeAllFilters = async () => {
  // Use viewState's removeAllFiltersAndReload which handles API calls
  await viewState.removeAllFiltersAndReload();
  currentPage.value = 1;
};

const handleAddEmployee = () => {
  employees.setCurrentEmployee(null);
  viewState.state.addEmployeeModal.show = true;
};

const handleSaveEmployee = async (employeeData) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const isNewEmployee = !employeeData?.id;
  
  const result = await viewState.handleEmployeeOperation(
    employees.operations.EDIT_EMPLOYEE,
    accountId,
    employeeData
  );
  
  // If it's a new employee and we got the created employee data, emit drilldown
  if (isNewEmployee && result?.createdEmployee?.id) {
    // Wait for route to update after navigation
    await proxy.$nextTick();
    
    // Emit drilldown event to immediately show employee detail
    // This ensures the UI updates immediately without waiting for route watcher
    emit('drilldown', {
      employeeId: String(result.createdEmployee.id),
      tab: 'pathtoreadiness'
    });
  }
};

const handleActivateEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.ACTIVATE_EMPLOYEE,
    accountId
  );
};

const handleDeleteEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.DELETE_EMPLOYEE,
    accountId
  );
};

const handleTerminateEmployee = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.TERMINATE_EMPLOYEE,
    accountId,
    payload
  );
};

const handleReactivateEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.REACTIVATE_EMPLOYEE,
    accountId
  );
};

const handleChangePicture = async (croppedImage) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.CHANGE_EMPLOYEE_PICTURE,
    accountId,
    croppedImage
  );
};

const handleRemovePicture = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.CHANGE_EMPLOYEE_PICTURE,
    accountId,
    null
  );
};

const handleCheckEmail = async (email) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.checkUserEmail({ accountId, email });
};

const handleGiveAccess = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.giveAccessToEmployee({
    accountId,
    payload,
  });
};

const handleGiveBackAccess = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.giveAccessToEmployee({
    accountId,
    payload,
  });
};

const handleArchiveEmployee = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await viewState.handleEmployeeOperation(
    employees.operations.ARCHIVE_EMPLOYEE,
    accountId
  );
};

const handleSuspendAccess = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const currentEmployee = employees.getCurrentEmployee();
  
  // Get access_id from various possible locations
  const access_id =
    currentEmployee?.access_id ||
    currentEmployee?.access?.access_id ||
    currentEmployee?.access?.id ||
    currentEmployee?.user?.id;
  
  if (!access_id) {
    proxy?.$toast?.error("Could not find access information for this employee.");
    viewState.state.suspendEmployeeAccessModal.show = false;
    return;
  }
  
  try {
    const obj = {
      email: currentEmployee?.email,
      state: "suspended",
    };
    
    await userStateUpdateSuspend(accountId, access_id, obj);
    viewState.state.suspendEmployeeAccessModal.show = false;
    await employees.loadAllEmployees();
  } catch (error) {
    console.error("Error suspending access:", error);
    proxy?.$toast?.error("Failed to suspend access. Please try again.");
  }
};

const handleGiveBackAccessUser = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  await userStateUpdateGiveBack({
    accountId,
    userId: employees.getCurrentEmployee()?.user?.id,
  });
  viewState.state.giveBackEmployeeAccessModal.show = false;
  await employees.loadAllEmployees();
};

// Initialize on mount
onMounted(async () => {
  // Set current page from viewState
  currentPage.value = viewState.state.table.currentPage || 1;
  itemsPerPage.value = viewState.state.table.itemsPerPage || 10;
  
  // Check if coming from dashboard with searchEmployee query parameter
  const route = proxy?.$route;
  const router = proxy?.$router;
  const fromDashboard = route?.query?.fromDashboard === 'true';
  const searchEmployeeName = route?.query?.searchEmployee || '';
  
  if (fromDashboard && searchEmployeeName && searchEmployeeName.trim() !== '') {
    // Wait a bit to ensure filters are cleared by OrganizationPage
    await proxy.$nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Decode the employee name
    const decodedName = decodeURIComponent(searchEmployeeName.trim());
    
    // Set search text in both state and local ref
    viewState.state.filters.searchText = decodedName;
    searchText.value = decodedName;
    
    // Wait for state to propagate
    await proxy.$nextTick();
    
    // Perform the search - this will load employees with the search filter
    await viewState.performSearch(decodedName);
    
    // Wait for search to complete
    await proxy.$nextTick();
    
    // Remove query parameters from URL after search is done
    if (router && route) {
      router.replace({
        path: route.path,
        query: {}
      });
    }
  }
});

// Clear search when component is unmounted (e.g., navigating to other tabs)
// This is a safety net in case the route watcher doesn't catch all navigation scenarios
onBeforeUnmount(() => {
  if (!proxy?.$route) return;
  
  const currentPath = proxy.$route.path || "";
  const currentTab = proxy.$route.params?.tab?.toLowerCase() || "";
  
  // Check if we're navigating to a different tab (not employees tab)
  // Employee detail pages (/new-organization/employees/123) are still within employees context
  const isEmployeeDetailPage = currentPath.match(/\/new-organization\/employees\/\d+/);
  const isStillOnEmployeesTab = currentTab === "employees" || isEmployeeDetailPage;
  
  // Clear search if navigating away from employees tab to a different tab
  if (!isStillOnEmployeesTab && viewState.state.filters.searchText) {
    viewState.clearSearch();
    searchText.value = "";
    // Also remove search filters from API
    employees.removeEmployeeFilter("first_name");
    employees.removeEmployeeFilter("last_name");
    employees.removeEmployeeFilter("name");
  }
});
</script>

<style scoped>
.icon-container {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}
.icon-svg {
  display: block;
}
::v-deep .custom-assign .allocation_icon > .content {
  top: 45%;
}
.employees-organization-table .ex-universal-table-container {
  box-shadow: 0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A !important;
  border-radius: 8px;
}
</style>
